import React, { useState, useEffect } from 'react';
import { 
  FiSearch, FiFileText, FiPrinter, FiX, FiPlus, 
  FiTrash2, FiShare2, FiArchive, FiCheck, FiRefreshCw 
} from 'react-icons/fi';
import Swal from 'sweetalert2';

const API_URL = 'https://app.thundermaxhome.com/api.php';

// ฟังก์ชันแปลงจำนวนเงินเป็นตัวอักษรภาษาไทย
function thaiBahtText(num) {
  if (num === null || num === undefined || isNaN(num)) return "";
  num = Number(num).toFixed(2);
  const [integerPart, decimalPart] = num.split('.');
  
  const thaiNums = ["ศูนย์", "หนึ่ง", "สอง", "สาม", "สี่", "ห้า", "หก", "เจ็ด", "แปด", "เก้า"];
  const thaiDigits = ["", "สิบ", "ร้อย", "พัน", "หมื่น", "แสน", "ล้าน"];
  
  const convert = (str) => {
    let len = str.length;
    let text = "";
    for (let i = 0; i < len; i++) {
      let digit = parseInt(str.charAt(i));
      let position = len - 1 - i;
      if (digit !== 0) {
        if (position === 1 && digit === 1) {
          text += "สิบ";
        } else if (position === 1 && digit === 2) {
          text += "ยี่สิบ";
        } else if (position === 0 && digit === 1 && len > 1) {
          text += "เอ็ด";
        } else {
          text += thaiNums[digit] + thaiDigits[position];
        }
      }
    }
    return text;
  };
  
  let result = "";
  if (parseInt(integerPart) === 0) {
    result += "ศูนย์บาท";
  } else {
    result += convert(integerPart) + "บาท";
  }
  
  if (parseInt(decimalPart) === 0) {
    result += "ถ้วน";
  } else {
    result += convert(decimalPart) + "สตางค์";
  }
  
  return result;
}

export default function InvoicePage() {
  const [activeTab, setActiveTab] = useState('issue'); // issue, archive
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [archiveSearch, setArchiveSearch] = useState('');
  
  // ประวัติเอกสารที่ออกแล้ว (เก็บใน localStorage)
  const [archivedInvoices, setArchivedInvoices] = useState([]);

  // States สำหรับบิลที่กำลังจัดการ
  const [selectedJob, setSelectedJob] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  
  // ฟิลด์ข้อมูลใบเสร็จ/ใบกำกับภาษี (ลูกค้า)
  const [docType, setDocType] = useState('receipt'); // receipt, tax_invoice
  const [docNo, setDocNo] = useState('');
  const [docDate, setDocDate] = useState(new Date().toISOString().split('T')[0]);
  const [customerName, setCustomerName] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerTaxId, setCustomerTaxId] = useState('');
  
  // รายการในบิล (Items)
  const [billItems, setBillItems] = useState([]);
  
  // ตัวเลือกภาษี
  const [vatType, setVatType] = useState('none'); // none, include, exclude
  const [withholdingTax, setWithholdingTax] = useState(0); // 0, 1, 3

  // รูปแบบการลงชื่อ
  const [signatureType, setSignatureType] = useState('online'); // manual (เซ็นมือ), online (อนุมัติออนไลน์/ประทับตรา), foot_note (คอมพิวเตอร์ออกเอกสาร ไม่ต้องเซ็น)

  // ข้อมูลร้านค้า (Company Profile) - เริ่มต้นด้วยค่าดีฟอลต์ แต่สามารถแก้ไขได้ในหน้าจอ
  const [companyName, setCompanyName] = useState('บริษัท ธันเดอร์แม็กซ์ โฮมเซอร์วิส จำกัด');
  const [companyAddress, setCompanyAddress] = useState('123/45 ถนนราชพฤกษ์ แขวงบางระมาด เขตตลิ่งชัน กรุงเทพมหานคร 10170');
  const [companyPhone, setCompanyPhone] = useState('02-123-4567, 089-999-9999');
  const [companyTaxId, setCompanyTaxId] = useState('0105562000123');

  // โหลดข้อมูลประวัติบิลจาก localStorage
  useEffect(() => {
    const saved = localStorage.getItem('archived_invoices');
    if (saved) {
      try {
        setArchivedInvoices(JSON.parse(saved));
      } catch (e) {
        console.error(e);
      }
    }
    fetchJobs();
  }, []);

  const fetchJobs = () => {
    setIsLoading(true);
    fetch(`${API_URL}?action=get_all_jobs`)
      .then(res => res.json())
      .then(data => {
        const completed = (data.data || []).filter(j => j.status === 'completed');
        setJobs(completed);
        setIsLoading(false);
      })
      .catch(err => {
        console.error(err);
        setIsLoading(false);
      });
  };

  const filteredJobs = jobs.filter(job => {
    const safeCustomer = job.customerName || '';
    const safePhone = job.phone || '';
    return safeCustomer.toLowerCase().includes(searchTerm.toLowerCase()) || safePhone.includes(searchTerm);
  });

  const filteredArchive = archivedInvoices.filter(inv => {
    const safeCustomer = inv.customerName || '';
    const safeDocNo = inv.docNo || '';
    return safeCustomer.toLowerCase().includes(archiveSearch.toLowerCase()) || safeDocNo.toLowerCase().includes(archiveSearch.toLowerCase());
  });

  // เมื่อเลือกงานเพื่อออกบิลใหม่
  const handleOpenEditor = (job) => {
    setSelectedJob(job);
    
    // ตั้งค่าเริ่มต้น
    const formattedDate = new Date().toISOString().split('T')[0].replace(/-/g, '');
    setDocNo(`INV-${formattedDate}-${job.id}`);
    setDocDate(new Date().toISOString().split('T')[0]);
    setCustomerName(job.customerName || '');
    setCustomerAddress(job.address || 'ไม่มีข้อมูลที่อยู่');
    setCustomerPhone(job.phone || '');
    setCustomerTaxId('');
    setDocType('receipt');
    setVatType('none');
    setWithholdingTax(0);
    setSignatureType('online');
    
    // โหลดรายการตั้งต้น
    setBillItems([
      {
        id: 1,
        description: `ค่าบริการงานซ่อม (${getJobTypeLabel(job.job_type)}) - ${job.problem || 'อาการทั่วไป'}`,
        qty: 1,
        unitPrice: Number(job.price) || 0
      }
    ]);
    
    setIsEditorOpen(true);
  };

  // เมื่อต้องการเขียนบิลเองแบบไม่มีงานอ้างอิง (สร้างบิลเปล่าจากศูนย์)
  const handleOpenManualEditor = () => {
    setSelectedJob({ id: 'manual-' + Date.now() }); // อ้างอิง ID จำลอง
    
    const today = new Date().toISOString().split('T')[0];
    const formattedDate = today.replace(/-/g, '');
    setDocNo(`INV-${formattedDate}-MANUAL`);
    setDocDate(today);
    setCustomerName('');
    setCustomerAddress('');
    setCustomerPhone('');
    setCustomerTaxId('');
    setDocType('receipt');
    setVatType('none');
    setWithholdingTax(0);
    setSignatureType('online');
    
    // โหลดรายการบริการเปล่า
    setBillItems([
      {
        id: 1,
        description: '',
        qty: 1,
        unitPrice: 0
      }
    ]);
    
    setIsEditorOpen(true);
  };

  const getJobTypeLabel = (type) => {
    const types = { 'aircon':'แอร์', 'electric':'ไฟฟ้า', 'contractor':'รับเหมา', 'extension':'ต่อเติม', 'solar':'โซล่าเซลล์', 'door':'ประตู', 'evertor':'ลิฟท์', 'camera':'กล้อง', 'rework':'งานแก้' };
    return types[type] || 'ทั่วไป';
  };

  // จัดการรายการสินค้า/บริการ
  const handleAddItem = () => {
    setBillItems([...billItems, { id: Date.now(), description: '', qty: 1, unitPrice: 0 }]);
  };

  const handleRemoveItem = (id) => {
    if (billItems.length === 1) return Swal.fire('แจ้งเตือน', 'ต้องมีอย่างน้อย 1 รายการในบิล', 'warning');
    setBillItems(billItems.filter(item => item.id !== id));
  };

  const handleUpdateItem = (id, field, value) => {
    setBillItems(billItems.map(item => {
      if (item.id === id) {
        return { ...item, [field]: value };
      }
      return item;
    }));
  };

  // คำนวณราคาทั้งหมด
  const subtotal = billItems.reduce((sum, item) => sum + (Number(item.qty) * Number(item.unitPrice) || 0), 0);
  
  let vat = 0;
  let baseAmount = subtotal;
  
  if (vatType === 'include') {
    vat = subtotal * 7 / 107;
    baseAmount = subtotal - vat;
  } else if (vatType === 'exclude') {
    vat = subtotal * 0.07;
    baseAmount = subtotal;
  }
  
  const totalWithVat = baseAmount + vat;
  const whtAmount = baseAmount * (Number(withholdingTax) / 100);
  const netAmount = totalWithVat - whtAmount;

  // บันทึกบิลลงในคลังประวัติ (localStorage)
  const saveInvoiceToArchive = () => {
    // ตรวจสอบว่ามีรายการซ้ำหรือไม่ ถ้าซ้ำจะอัปเดต ถ้าไม่ซ้ำจะเพิ่มใหม่
    const newInvoice = {
      id: selectedJob?.id || Date.now(),
      docNo,
      docDate,
      docType,
      customerName,
      customerAddress,
      customerPhone,
      customerTaxId,
      billItems,
      vatType,
      withholdingTax,
      signatureType,
      subtotal,
      vat,
      whtAmount,
      netAmount,
      companyName,
      companyAddress,
      companyPhone,
      companyTaxId,
      createdAt: new Date().toISOString()
    };

    let updatedList = [];
    const exists = archivedInvoices.find(inv => inv.docNo === docNo);
    
    if (exists) {
      updatedList = archivedInvoices.map(inv => inv.docNo === docNo ? newInvoice : inv);
    } else {
      updatedList = [newInvoice, ...archivedInvoices];
    }

    localStorage.setItem('archived_invoices', JSON.stringify(updatedList));
    setArchivedInvoices(updatedList);
  };

  // สั่งพิมพ์บิลจริง
  const handlePrintInvoice = () => {
    saveInvoiceToArchive();
    window.print();
  };

  // ดึงบิลเก่าจากคลังมาเปิดซ้ำ / แก้ไข
  const handleLoadFromArchive = (inv) => {
    setSelectedJob({ id: inv.id }); // จำลอง job id
    setDocNo(inv.docNo);
    setDocDate(inv.docDate);
    setDocType(inv.docType);
    setCustomerName(inv.customerName);
    setCustomerAddress(inv.customerAddress);
    setCustomerPhone(inv.customerPhone);
    setCustomerTaxId(inv.customerTaxId || '');
    setBillItems(inv.billItems);
    setVatType(inv.vatType);
    setWithholdingTax(inv.withholdingTax);
    setSignatureType(inv.signatureType || 'online');
    
    // ตั้งค่าข้อมูลบริษัทตามที่เคยเซฟไว้ในเอกสารนั้น
    setCompanyName(inv.companyName || 'บริษัท ธันเดอร์แม็กซ์ โฮมเซอร์วิส จำกัด');
    setCompanyAddress(inv.companyAddress || '123/45 ถนนราชพฤกษ์ แขวงบางระมาด เขตตลิ่งชัน กรุงเทพมหานคร 10170');
    setCompanyPhone(inv.companyPhone || '02-123-4567, 089-999-9999');
    setCompanyTaxId(inv.companyTaxId || '0105562000123');

    setIsEditorOpen(true);
    // เปลี่ยนแท็บกลับมาเพื่อดูตัวพรีวิว
    setActiveTab('issue');
  };

  // ลบบิลออกจากคลังเก็บประวัติ
  const handleDeleteArchive = (docNoToDelete, e) => {
    e.stopPropagation();
    Swal.fire({
      title: 'ยืนยันการลบ?',
      text: `คุณต้องการลบประวัติบิลเลขที่ ${docNoToDelete} หรือไม่?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'ยืนยันการลบ',
      cancelButtonText: 'ยกเลิก'
    }).then((result) => {
      if (result.isConfirmed) {
        const updated = archivedInvoices.filter(inv => inv.docNo !== docNoToDelete);
        localStorage.setItem('archived_invoices', JSON.stringify(updated));
        setArchivedInvoices(updated);
        Swal.fire('สำเร็จ', 'ลบประวัติบิลเรียบร้อยแล้ว', 'success');
      }
    });
  };

  // คัดกรองข้อความสรุปสำหรับส่งใน LINE
  const handleCopyForLine = () => {
    const docLabel = docType === 'receipt' ? 'ใบเสร็จรับเงิน (e-Receipt)' : 'ใบกำกับภาษี (e-Tax Invoice)';
    const textSummary = `📄 ${docLabel} ออนไลน์
----------------------------------
เลขที่เอกสาร: ${docNo}
วันที่ออกบิล: ${new Date(docDate).toLocaleDateString('th-TH')}
ผู้ให้บริการ: ${companyName}
เลขผู้เสียภาษีผู้ขาย: ${companyTaxId}
----------------------------------
ชื่อลูกค้า: ${customerName}
เบอร์โทรศัพท์: ${customerPhone}
----------------------------------
รายการบริการ:
${billItems.map((item, idx) => `${idx + 1}. ${item.description} (x${item.qty}) - ฿${(Number(item.qty) * Number(item.unitPrice)).toLocaleString()}`).join('\n')}
----------------------------------
ยอดสุทธิชำระจริง: ฿${netAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
ตัวอักษร: (${thaiBahtText(netAmount)})
สถานะ: ชำระเงินเรียบร้อยแล้ว [ผ่านช่องทางออนไลน์/ระบบส่วนกลาง]
----------------------------------
*เอกสารฉบับนี้ได้รับอนุมัติในรูปแบบอิเล็กทรอนิกส์แล้ว*`;

    navigator.clipboard.writeText(textSummary).then(() => {
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'คัดลอกข้อความสรุปสำหรับแชร์ทาง LINE เรียบร้อยแล้ว!',
        showConfirmButton: false,
        timer: 2500
      });
    }).catch(err => {
      console.error(err);
      Swal.fire('ข้อผิดพลาด', 'ไม่สามารถคัดลอกข้อความได้', 'error');
    });
  };

  if (isPreviewMode) {
    return (
      <div className="fixed inset-0 z-50 bg-[#323639] overflow-y-auto p-4 md:p-8 flex flex-col items-center print:bg-white print:p-0">
        <style dangerouslySetInnerHTML={{__html: `
          @media print {
            body * {
              visibility: hidden !important;
            }
            .print-invoice-container,
            .print-invoice-container * {
              visibility: visible !important;
            }
            .print-invoice-container {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              width: 100% !important;
              border: none !important;
              box-shadow: none !important;
              padding: 0 !important;
              margin: 0 !important;
              background: white !important;
              color: black !important;
              display: block !important;
              min-height: auto !important;
            }
            @page {
              size: A4;
              margin: 1.5cm 1cm 1.5cm 1cm;
            }
          }
        `}} />
        
        {/* แถบเครื่องมือพรีวิว (ซ่อนเมื่อสั่งพิมพ์) */}
        <div className="preview-toolbar w-full max-w-[210mm] bg-[#202124] text-white p-4 rounded-t-2xl flex justify-between items-center shadow-md print:hidden">
          <div className="flex items-center gap-2">
            <span className="text-sm font-black text-indigo-400">👀 ตัวอย่างก่อนพิมพ์ A4 (Preview)</span>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={() => setIsPreviewMode(false)}
              className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5"
            >
              <FiX size={14} /> ย้อนกลับไปแก้ไข
            </button>
            <button 
              onClick={() => window.print()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 shadow-md"
            >
              <FiPrinter size={14} /> สั่งพิมพ์ / บันทึก PDF
            </button>
          </div>
        </div>

        {/* กระดาษใบเสร็จ (A4) */}
        <div className="bg-white p-8 shadow-2xl text-black print:border-none print:shadow-none print:p-0 min-h-[297mm] w-full max-w-[210mm] flex flex-col justify-between rounded-b-2xl print:rounded-none print-invoice-container">
          <div>
            {/* ส่วนหัวบริษัท */}
            <div className="flex justify-between items-start gap-4 pb-6 border-b border-gray-300">
              <div className="space-y-1 flex-1 text-left">
                <h3 className="text-base font-black text-gray-900">{companyName}</h3>
                <p className="text-[10px] text-gray-600 leading-relaxed max-w-sm">{companyAddress}</p>
                <p className="text-[10px] text-gray-600">📞 โทร: {companyPhone}</p>
                <p className="text-[10px] text-gray-600">เลขประจำตัวผู้เสียภาษี: <span className="font-bold">{companyTaxId}</span></p>
              </div>
              <div className="text-right space-y-2">
                <div className="bg-gray-100 px-4 py-2 rounded-lg inline-block border border-gray-200">
                  <h2 className="text-sm font-black text-indigo-700 uppercase tracking-wider">
                    {docType === 'receipt' ? 'ใบเสร็จรับเงิน' : 'ใบกำกับภาษี'}
                  </h2>
                  <p className="text-[8px] text-gray-500 font-bold">RECEIPT / TAX INVOICE</p>
                </div>
                <div className="text-[10px] text-gray-700 space-y-0.5">
                  <p>เลขที่เอกสาร: <span className="font-bold">{docNo}</span></p>
                  <p>วันที่ออกเอกสาร: <span className="font-bold">{new Date(docDate).toLocaleDateString('th-TH')}</span></p>
                </div>
              </div>
            </div>

            {/* ข้อมูลลูกค้าในเอกสาร */}
            <div className="grid grid-cols-2 gap-4 py-6 border-b border-gray-200 text-left">
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">ลูกค้าผู้รับบริการ (Client Info)</p>
                <p className="text-xs font-bold text-gray-900">{customerName}</p>
                <p className="text-[10px] text-gray-600 leading-relaxed">{customerAddress}</p>
                <p className="text-[10px] text-gray-600">📞 โทร: {customerPhone}</p>
              </div>
              <div className="space-y-1 text-right self-end">
                {docType === 'tax_invoice' && customerTaxId && (
                  <p className="text-[10px] text-gray-800">
                    เลขประจำตัวผู้เสียภาษี: <span className="font-bold">{customerTaxId}</span>
                  </p>
                )}
              </div>
            </div>

            {/* ตารางแสดงรายการ */}
            <table className="w-full text-left text-xs border-collapse mt-6">
              <thead>
                <tr className="bg-gray-50 border-y border-gray-300 text-gray-700 font-bold">
                  <th className="p-3 pl-4 text-center w-12">ลำดับ</th>
                  <th className="p-3">คำอธิบายรายการ (Description)</th>
                  <th className="p-3 text-center w-16">จำนวน</th>
                  <th className="p-3 text-right w-28">ราคาต่อหน่วย</th>
                  <th className="p-3 text-right pr-4 w-28">จำนวนเงิน (บาท)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {billItems.map((item, index) => {
                  const amount = Number(item.qty) * Number(item.unitPrice) || 0;
                  return (
                    <tr key={item.id}>
                      <td className="p-3 text-center text-gray-500 font-medium">{index + 1}</td>
                      <td className="p-3 font-semibold text-gray-800">{item.description || '-'}</td>
                      <td className="p-3 text-center font-medium">{item.qty}</td>
                      <td className="p-3 text-right font-medium">{Number(item.unitPrice).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      <td className="p-3 text-right pr-4 font-bold text-gray-900">{amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ส่วนสรุปการเงินด้านล่าง */}
          <div className="mt-8 space-y-6 text-left">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
              {/* คำอ่านภาษาไทย */}
              <div className="md:col-span-7 bg-gray-50 p-3 rounded-lg border border-gray-200 text-center font-bold text-[10px] text-gray-600 flex justify-center items-center h-full min-h-12">
                ({thaiBahtText(netAmount)})
              </div>

              {/* ตัวเลขคำนวณ */}
              <div className="md:col-span-5 text-right space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">ราคารวม (Subtotal):</span>
                  <span className="font-bold">฿{subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
                {vatType !== 'none' && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">ฐานภาษี (Base Amount):</span>
                    <span>฿{baseAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                {vatType !== 'none' && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">ภาษีมูลค่าเพิ่ม (VAT 7%):</span>
                    <span>฿{vat.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                {withholdingTax > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span className="font-medium">หักภาษี ณ ที่จ่าย ({withholdingTax}%):</span>
                    <span>- ฿{whtAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm border-t border-gray-300 pt-2 text-indigo-700 font-black">
                  <span>ยอดสุทธิที่ต้องชำระ (Net Total):</span>
                  <span>฿{netAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>

            {/* ส่วนลายเซ็น */}
            <div className="pt-6 border-t border-gray-100">
              {signatureType === 'online' && (
                <div className="flex flex-col items-center justify-center py-4 bg-emerald-50 border border-emerald-200 rounded-xl max-w-sm mx-auto text-center">
                  <div className="border-4 border-emerald-600 text-emerald-600 font-black text-xs px-4 py-2 uppercase rounded-lg transform -rotate-3 inline-block tracking-wider">
                    ชำระเงินเรียบร้อย
                    <p className="text-[8px] font-bold text-center">PAID / ONLINE SYSTEM</p>
                  </div>
                  <p className="text-[8px] text-emerald-700 font-bold mt-2">
                    เอกสารฉบับนี้ออกด้วยระบบอิเล็กทรอนิกส์
                  </p>
                  <p className="text-[7px] text-gray-400">
                    ยืนยันการชำระเงินของลูกค้าและสรุปรายการบริการเรียบร้อยแล้ว
                  </p>
                </div>
              )}

              {signatureType === 'manual' && (
                <div className="grid grid-cols-2 gap-8 text-center text-xs">
                  <div className="space-y-12">
                    <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wide">ผู้รับเงิน (Recipient Signature)</p>
                    <div className="border-t border-dashed border-gray-400 w-44 mx-auto pt-2">
                      <p className="font-bold text-gray-800">วันที่ ......./......./.......</p>
                    </div>
                  </div>
                  <div className="space-y-12">
                    <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wide">ผู้มีอำนาจลงนาม (Authorized Signature)</p>
                    <div className="border-t border-dashed border-gray-400 w-44 mx-auto pt-2">
                      <p className="font-bold text-gray-800">ผู้รับมอบอำนาจ</p>
                    </div>
                  </div>
                </div>
              )}

              {signatureType === 'foot_note' && (
                <div className="text-center py-4 text-gray-500 text-[10px] italic space-y-1 max-w-md mx-auto">
                  <p className="font-bold">🚨 คำชี้แจงสถานะเอกสารแบบออนไลน์</p>
                  <p>“เอกสารฉบับนี้เป็นเอกสารรับเงินที่จัดทำผ่านระบบคอมพิวเตอร์ส่วนกลางแบบ e-Receipt การยืนยันชำระเงินและการส่งมอบบริการถือเป็นอันเสร็จสิ้นโดยอัตโนมัติ จึงไม่จำเป็นต้องมีลายมือชื่อผู้ลงนามทางกายภาพ”</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10 animate-fade-in text-gray-800 dark:text-gray-100 print:bg-white print:p-0 print:text-black">
      <style dangerouslySetInnerHTML={{__html: `
        @media print {
          body * {
            visibility: hidden !important;
          }
          .print-invoice-container,
          .print-invoice-container * {
            visibility: visible !important;
          }
          .print-invoice-container {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            border: none !important;
            box-shadow: none !important;
            padding: 0 !important;
            margin: 0 !important;
            background: white !important;
            color: black !important;
            display: block !important;
            min-height: auto !important;
          }
          @page {
            size: A4;
            margin: 1.5cm 1cm 1.5cm 1cm;
          }
        }
      `}} />
      
      {/* แท็บสลับหน้าจอ (ซ่อนเมื่อพิมพ์) */}
      <div className="flex justify-between items-center print:hidden border-b border-gray-200 dark:border-[#334155] pb-1">
        <div className="flex gap-4">
          <button 
            onClick={() => { setActiveTab('issue'); }}
            className={`pb-3 text-sm font-bold transition-all relative ${activeTab === 'issue' ? 'text-indigo-600 dark:text-indigo-400 font-extrabold border-b-2 border-indigo-600 dark:border-indigo-400' : 'text-gray-400 hover:text-gray-600'}`}
          >
            ➕ ออกใบเสร็จ / ใบกำกับภาษีใหม่
          </button>
          <button 
            onClick={() => { setActiveTab('archive'); }}
            className={`pb-3 text-sm font-bold transition-all relative ${activeTab === 'archive' ? 'text-indigo-600 dark:text-indigo-400 font-extrabold border-b-2 border-indigo-600 dark:border-indigo-400' : 'text-gray-400 hover:text-gray-600'}`}
          >
            📂 คลังประวัติเอกสารที่ออกแล้ว ({archivedInvoices.length})
          </button>
        </div>
        {activeTab === 'issue' && (
          <button 
            onClick={fetchJobs} 
            className="p-2 text-indigo-600 dark:text-indigo-400 hover:bg-gray-100 dark:hover:bg-[#1e293b] rounded-lg transition-colors flex items-center gap-1 text-xs font-bold"
            title="รีเฟรชงาน"
          >
            <FiRefreshCw size={12} /> รีเฟรชรายชื่องาน
          </button>
        )}
      </div>

      {/* 1. แท็บออกใบเสร็จใหม่ (ซ่อนเมื่อพิมพ์) */}
      {activeTab === 'issue' && !isEditorOpen && (
        <div className="print:hidden space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl font-black">🧾 รายการงานซ่อมซ่อมเสร็จพร้อมออกบิล</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                เลือกรายการงานที่สำเร็จด้านล่าง หรือกดปุ่มเขียนบิลเปล่าเพื่อสร้างเอกสารใหม่โดยไม่อ้างอิงงานซ่อม
              </p>
            </div>
            <button 
              onClick={handleOpenManualEditor}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-2 shadow-md transition shrink-0"
            >
              <FiPlus size={14} /> 📝 สร้างบิลเปล่า (เขียนเอง)
            </button>
          </div>

          {/* ค้นหา */}
          <div className="relative w-full max-w-md">
            <FiSearch className="absolute left-3 top-3 text-gray-400" />
            <input 
              type="text" 
              placeholder="ค้นหาชื่อลูกค้า, เบอร์โทรศัพท์..." 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
              className="w-full pl-10 pr-4 py-2 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-[#334155] rounded-xl text-sm dark:text-white outline-none focus:border-blue-500 transition-colors" 
            />
          </div>

          {/* ตารางงาน */}
          <div className="bg-white dark:bg-[#1e293b] rounded-2xl border border-gray-200 dark:border-[#334155] shadow-sm overflow-hidden transition-colors">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100 dark:bg-[#0f172a] text-gray-600 dark:text-gray-400 text-xs font-bold border-b border-gray-200 dark:border-[#334155] transition-colors">
                    <th className="p-4 pl-6">วันที่สำเร็จ</th>
                    <th className="p-4">ชื่อลูกค้า / เบอร์โทร</th>
                    <th className="p-4">ประเภทงาน</th>
                    <th className="p-4">รายละเอียดปัญหาหน้างาน</th>
                    <th className="p-4 text-right">ยอดราคาปิดงาน</th>
                    <th className="p-4 pr-6 text-center">ดำเนินการ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-[#334155] transition-colors">
                  {isLoading ? (
                    <tr><td colSpan="6" className="p-10 text-center text-blue-500 font-bold">กำลังโหลดงานซ่อม...</td></tr>
                  ) : filteredJobs.length > 0 ? (
                    filteredJobs.map((job) => (
                      <tr key={job.id} className="hover:bg-gray-50 dark:hover:bg-[#131825]/50 transition-colors">
                        <td className="p-4 pl-6 text-xs text-gray-400">
                          {new Date(job.createdAt).toLocaleDateString('th-TH')}
                        </td>
                        <td className="p-4">
                          <span className="font-bold text-gray-800 dark:text-white">{job.customerName || '-'}</span>
                          <p className="text-[10px] text-gray-400">📞 {job.phone || '-'}</p>
                        </td>
                        <td className="p-4">
                          <span className="px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-[10px] font-bold">
                            {getJobTypeLabel(job.job_type)}
                          </span>
                        </td>
                        <td className="p-4 text-xs text-gray-500 max-w-xs truncate" title={job.problem}>
                          {job.problem || '-'}
                        </td>
                        <td className="p-4 text-right font-black text-green-600 dark:text-green-400">
                          ฿{Number(job.price).toLocaleString()}
                        </td>
                        <td className="p-4 pr-6 text-center">
                          <button 
                            onClick={() => handleOpenEditor(job)}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 mx-auto transition shadow-sm"
                          >
                            <FiFileText size={12} /> ออกบิลเอกสาร
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="p-10 text-center text-gray-400">ไม่มีรายการปิดงานใหม่ที่ยังไม่ได้ออกบิล</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* 2. แท็บคลังประวัติเอกสารที่เคยออกบิลแล้ว (ซ่อนเมื่อพิมพ์) */}
      {activeTab === 'archive' && (
        <div className="print:hidden space-y-6">
          <div>
            <h1 className="text-xl font-black">📂 คลังประวัติใบเสร็จและใบกำกับภาษี</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              ประวัติบิลทั้งหมดที่เซฟเก็บไว้ คุณสามารถเรียกดูเพื่อสั่งพิมพ์ซ้ำ แก้ไข หรือคัดลอกรายละเอียดไปส่งให้ลูกค้าทาง LINE ได้ทันที
            </p>
          </div>

          {/* ค้นหา */}
          <div className="relative w-full max-w-md">
            <FiSearch className="absolute left-3 top-3 text-gray-400" />
            <input 
              type="text" 
              placeholder="ค้นหาชื่อลูกค้า, เลขที่บิล..." 
              value={archiveSearch} 
              onChange={(e) => setArchiveSearch(e.target.value)} 
              className="w-full pl-10 pr-4 py-2 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-[#334155] rounded-xl text-sm dark:text-white outline-none focus:border-blue-500 transition-colors" 
            />
          </div>

          {/* ตารางเอกสารในคลัง */}
          <div className="bg-white dark:bg-[#1e293b] rounded-2xl border border-gray-200 dark:border-[#334155] shadow-sm overflow-hidden transition-colors">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100 dark:bg-[#0f172a] text-gray-600 dark:text-gray-400 text-xs font-bold border-b border-gray-200 dark:border-[#334155] transition-colors">
                    <th className="p-4 pl-6">เลขที่เอกสาร</th>
                    <th className="p-4">ประเภท</th>
                    <th className="p-4">วันที่ออกเอกสาร</th>
                    <th className="p-4">ลูกค้าผู้รับบิล</th>
                    <th className="p-4 text-right">ยอดเงินสุทธิ</th>
                    <th className="p-4 pr-6 text-center">จัดการเอกสาร</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-[#334155] transition-colors">
                  {filteredArchive.length > 0 ? (
                    filteredArchive.map((inv) => (
                      <tr 
                        key={inv.docNo} 
                        onClick={() => handleLoadFromArchive(inv)}
                        className="hover:bg-gray-50 dark:hover:bg-[#131825]/50 transition-colors cursor-pointer"
                      >
                        <td className="p-4 pl-6 font-bold text-indigo-600 dark:text-indigo-400">{inv.docNo}</td>
                        <td className="p-4">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${inv.docType === 'receipt' ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                            {inv.docType === 'receipt' ? 'ใบเสร็จรับเงิน' : 'ใบกำกับภาษี'}
                          </span>
                        </td>
                        <td className="p-4 text-xs">
                          {new Date(inv.docDate).toLocaleDateString('th-TH')}
                        </td>
                        <td className="p-4 font-semibold">
                          {inv.customerName}
                          <p className="text-[10px] text-gray-400 font-normal">📞 {inv.customerPhone || '-'}</p>
                        </td>
                        <td className="p-4 text-right font-black text-indigo-600 dark:text-indigo-400">
                          ฿{Number(inv.netAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td className="p-4 pr-6 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleLoadFromArchive(inv);
                                // คัดลอก LINE ทันที
                                setTimeout(() => handleCopyForLine(), 200);
                              }}
                              className="p-2 bg-green-50 text-green-600 hover:bg-green-100 rounded-lg text-xs font-bold transition"
                              title="คัดลอกข้อความ LINE"
                            >
                              <FiShare2 size={12} /> LINE
                            </button>
                            <button 
                              onClick={(e) => { e.stopPropagation(); handleLoadFromArchive(inv); }}
                              className="p-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg text-xs font-bold transition"
                              title="เรียกดู / พิมพ์"
                            >
                              <FiPrinter size={12} /> พิมพ์
                            </button>
                            <button 
                              onClick={(e) => handleDeleteArchive(inv.docNo, e)}
                              className="p-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg text-xs font-bold transition"
                              title="ลบออกจากคลัง"
                            >
                              <FiTrash2 size={12} /> ลบ
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="p-10 text-center text-gray-400">ไม่มีเอกสารบันทึกในประวัติ</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* 3. ส่วนการดีไซน์และพรีวิวใบเสร็จ (แสดงเมื่อเปิดห้องสร้างบิล) */}
      {isEditorOpen && selectedJob && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 print:block">
          
          {/* แผงควบคุมบิลทางซ้าย (ซ่อนเมื่อสั่งพิมพ์) */}
          <div className="lg:col-span-5 bg-white dark:bg-[#1e293b] p-5 rounded-2xl border border-gray-200 dark:border-[#334155] shadow-sm space-y-4 print:hidden transition-colors max-h-[85vh] overflow-y-auto pr-1">
            <div className="flex justify-between items-center pb-2 border-b border-gray-200 dark:border-[#334155]">
              <h2 className="font-black text-base text-indigo-600 dark:text-indigo-400">🛠️ แผงควบคุมบิลและการเงิน</h2>
              <button 
                onClick={() => setIsEditorOpen(false)}
                className="p-1 bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-500 rounded-lg transition"
              >
                <FiX size={18} />
              </button>
            </div>

            {/* ส่วนหัว: ข้อมูลบริษัทผู้รับเงิน (แก้ไขได้แบบเรียลไทม์) */}
            <div className="space-y-2 p-3 bg-indigo-50/50 dark:bg-[#0f172a]/40 rounded-xl border border-indigo-100/50 dark:border-[#334155]">
              <h3 className="text-xs font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-wide">🏢 ข้อมูลบริษัทของคุณ (ผู้รับเงิน)</h3>
              <div className="space-y-1">
                <label className="text-[10px] text-gray-400">ชื่อบริษัท / ร้านค้า</label>
                <input 
                  type="text" 
                  value={companyName} 
                  onChange={e => setCompanyName(e.target.value)} 
                  className="w-full p-2 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-[#334155] rounded-lg text-xs dark:text-white" 
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] text-gray-400">ที่อยู่ผู้ให้บริการ</label>
                <textarea 
                  value={companyAddress} 
                  onChange={e => setCompanyAddress(e.target.value)} 
                  rows="2"
                  className="w-full p-2 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-[#334155] rounded-lg text-xs dark:text-white resize-none" 
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-[10px] text-gray-400">เบอร์โทรศัพท์ติดต่อ</label>
                  <input 
                    type="text" 
                    value={companyPhone} 
                    onChange={e => setCompanyPhone(e.target.value)} 
                    className="w-full p-2 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-[#334155] rounded-lg text-xs dark:text-white" 
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] text-gray-400">เลขผู้เสียภาษีบริษัท (13 หลัก)</label>
                  <input 
                    type="text" 
                    value={companyTaxId} 
                    onChange={e => setCompanyTaxId(e.target.value)} 
                    className="w-full p-2 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-[#334155] rounded-lg text-xs dark:text-white" 
                  />
                </div>
              </div>
            </div>

            {/* การแชร์ข้อมูล */}
            <div className="p-3 bg-emerald-50/50 dark:bg-emerald-950/20 rounded-xl border border-emerald-100/50 dark:border-emerald-900/50 flex justify-between items-center">
              <div>
                <h4 className="text-xs font-bold text-emerald-800 dark:text-emerald-400">💬 ส่งบิลผ่าน LINE</h4>
                <p className="text-[10px] text-gray-400">คัดลอกข้อความสรุปสำหรับส่งในแชท</p>
              </div>
              <button 
                onClick={handleCopyForLine}
                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 transition"
              >
                <FiShare2 size={12} /> คัดลอก LINE
              </button>
            </div>

            {/* ประเภทเอกสาร */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-gray-400">ประเภทเอกสาร</label>
              <div className="grid grid-cols-2 gap-2">
                <button 
                  onClick={() => setDocType('receipt')}
                  className={`py-2 px-3 rounded-lg text-xs font-bold transition border ${docType === 'receipt' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-gray-50 dark:bg-[#0f172a] text-gray-600 dark:text-gray-300 border-gray-300 dark:border-[#334155]'}`}
                >
                  🧾 ใบเสร็จรับเงิน
                </button>
                <button 
                  onClick={() => setDocType('tax_invoice')}
                  className={`py-2 px-3 rounded-lg text-xs font-bold transition border ${docType === 'tax_invoice' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-gray-50 dark:bg-[#0f172a] text-gray-600 dark:text-gray-300 border-gray-300 dark:border-[#334155]'}`}
                >
                  🏢 ใบกำกับภาษี
                </button>
              </div>
            </div>

            {/* เลขที่ & วันที่ */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400">เลขที่เอกสาร</label>
                <input 
                  type="text" 
                  value={docNo} 
                  onChange={e => setDocNo(e.target.value)} 
                  className="w-full p-2 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-lg text-xs dark:text-white" 
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400">วันที่ออกเอกสาร</label>
                <input 
                  type="date" 
                  value={docDate} 
                  onChange={e => setDocDate(e.target.value)} 
                  className="w-full p-2 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-lg text-xs dark:text-white" 
                />
              </div>
            </div>

            {/* ข้อมูลลูกค้า */}
            <div className="space-y-2 pb-2 border-b border-gray-200 dark:border-[#334155]">
              <h4 className="text-xs font-black text-indigo-500 uppercase">ข้อมูลลูกค้าผู้รับบิล</h4>
              <div className="space-y-1">
                <label className="text-[10px] text-gray-400">ชื่อลูกค้า / บริษัท</label>
                <input 
                  type="text" 
                  value={customerName} 
                  onChange={e => setCustomerName(e.target.value)} 
                  className="w-full p-2 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-lg text-xs dark:text-white" 
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] text-gray-400">ที่อยู่ผู้รับบิล</label>
                <textarea 
                  value={customerAddress} 
                  onChange={e => setCustomerAddress(e.target.value)} 
                  rows="2"
                  className="w-full p-2 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-lg text-xs dark:text-white resize-none" 
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] text-gray-400">เบอร์โทรศัพท์ลูกค้า</label>
                  <input 
                    type="text" 
                    value={customerPhone} 
                    onChange={e => setCustomerPhone(e.target.value)} 
                    className="w-full p-2 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-lg text-xs dark:text-white" 
                  />
                </div>
                {docType === 'tax_invoice' && (
                  <div className="space-y-1">
                    <label className="text-[10px] text-gray-400">เลขผู้เสียภาษีลูกค้า (13 หลัก)</label>
                    <input 
                      type="text" 
                      value={customerTaxId} 
                      onChange={e => setCustomerTaxId(e.target.value)} 
                      className="w-full p-2 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-lg text-xs dark:text-white" 
                      placeholder="เลขประจำตัวผู้เสียภาษี"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* รายละเอียดบริการ */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <h4 className="text-xs font-black text-indigo-500 uppercase">รายละเอียดบริการในบิล</h4>
                <button 
                  onClick={handleAddItem}
                  className="text-xs font-bold text-blue-600 dark:text-blue-400 flex items-center gap-1 hover:underline"
                >
                  <FiPlus size={12} /> เพิ่มรายการ
                </button>
              </div>
              
              <div className="space-y-2 max-h-44 overflow-y-auto pr-1">
                {billItems.map((item, idx) => (
                  <div key={item.id} className="p-3 bg-gray-50 dark:bg-[#0f172a] rounded-xl border border-gray-200 dark:border-[#334155] space-y-2 relative">
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] font-bold text-gray-400">รายการที่ {idx + 1}</span>
                      <button 
                        onClick={() => handleRemoveItem(item.id)}
                        className="text-red-500 hover:text-red-700 transition"
                      >
                        <FiTrash2 size={12} />
                      </button>
                    </div>
                    <input 
                      type="text" 
                      placeholder="คำอธิบายบริการ / อะไหล่"
                      value={item.description} 
                      onChange={e => handleUpdateItem(item.id, 'description', e.target.value)} 
                      className="w-full p-1.5 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-[#334155] rounded-lg text-xs dark:text-white" 
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input 
                        type="number" 
                        placeholder="จำนวน"
                        value={item.qty} 
                        onChange={e => handleUpdateItem(item.id, 'qty', parseInt(e.target.value) || 0)} 
                        className="w-full p-1.5 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-[#334155] rounded-lg text-xs dark:text-white" 
                      />
                      <input 
                        type="number" 
                        placeholder="ราคาต่อหน่วย"
                        value={item.unitPrice} 
                        onChange={e => handleUpdateItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)} 
                        className="w-full p-1.5 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-[#334155] rounded-lg text-xs dark:text-white text-right" 
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ภาษีและหัก ณ ที่จ่าย */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-gray-400">การคิดภาษี VAT 7%</label>
                <select 
                  value={vatType} 
                  onChange={e => setVatType(e.target.value)} 
                  className="w-full p-2 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-lg text-xs font-bold text-gray-700 dark:text-gray-300"
                >
                  <option value="none">ไม่มีการคิด VAT</option>
                  <option value="include">ราคารวม VAT 7% แล้ว</option>
                  <option value="exclude">ราคาแยก VAT 7% (บวกนอก)</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-gray-400">หักภาษี ณ ที่จ่าย</label>
                <select 
                  value={withholdingTax} 
                  onChange={e => setWithholdingTax(Number(e.target.value))} 
                  className="w-full p-2 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-lg text-xs font-bold text-gray-700 dark:text-gray-300"
                >
                  <option value="0">ไม่มีการหัก</option>
                  <option value="1">หักภาษี ณ ที่จ่าย 1%</option>
                  <option value="3">หักภาษี ณ ที่จ่าย 3%</option>
                </select>
              </div>
            </div>

            {/* วิธีการลงชื่อผู้รับเงิน/ผู้ออกเอกสาร */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-gray-400">รูปแบบการลงชื่อของเอกสาร</label>
              <select 
                value={signatureType} 
                onChange={e => setSignatureType(e.target.value)} 
                className="w-full p-2 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-lg text-xs font-bold text-gray-700 dark:text-gray-300"
              >
                <option value="online">✅ อนุมัติผ่านระบบคอมพิวเตอร์ออนไลน์ (ไม่มีช่องเซ็น)</option>
                <option value="manual">✍️ แสดงลายเส้นประสำหรับเซ็นด้วยปากกาจริง</option>
                <option value="foot_note">📄 ออกด้วยระบบอัตโนมัติ (พร้อมข้อความประกาศงดลายเซ็น)</option>
              </select>
            </div>

            {/* ปุ่มกดสั่งพิมพ์และบันทึกคลัง */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <button 
                onClick={saveInvoiceToArchive}
                className="bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-bold py-2.5 rounded-xl flex justify-center items-center gap-1.5 text-xs shadow-sm transition"
              >
                <FiArchive size={14} /> บันทึกลงประวัติ
              </button>
              <button 
                onClick={() => { saveInvoiceToArchive(); setIsPreviewMode(true); }}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 rounded-xl flex justify-center items-center gap-1.5 text-xs shadow-md transition"
              >
                <FiPrinter size={14} /> ดูตัวอย่างบิล / สั่งพิมพ์
              </button>
            </div>
          </div>

          {/* ตัวบิลสำหรับพิมพ์ (A4 Preview) */}
          <div className="lg:col-span-7 bg-white p-8 rounded-2xl border border-gray-200 shadow-md text-black print:border-none print:shadow-none print:p-0 min-h-[297mm] flex flex-col justify-between print-invoice-container">
            
            <div>
              {/* ส่วนหัวบริษัท */}
              <div className="flex justify-between items-start gap-4 pb-6 border-b border-gray-300">
                <div className="space-y-1 flex-1">
                  <h3 className="text-base font-black text-gray-900">{companyName}</h3>
                  <p className="text-[10px] text-gray-600 leading-relaxed max-w-sm">{companyAddress}</p>
                  <p className="text-[10px] text-gray-600">📞 โทร: {companyPhone}</p>
                  <p className="text-[10px] text-gray-600">เลขประจำตัวผู้เสียภาษี: <span className="font-bold">{companyTaxId}</span></p>
                </div>
                <div className="text-right space-y-2">
                  <div className="bg-gray-100 px-4 py-2 rounded-lg inline-block border border-gray-200">
                    <h2 className="text-sm font-black text-indigo-700 uppercase tracking-wider">
                      {docType === 'receipt' ? 'ใบเสร็จรับเงิน' : 'ใบกำกับภาษี'}
                    </h2>
                    <p className="text-[8px] text-gray-500 font-bold">RECEIPT / TAX INVOICE</p>
                  </div>
                  <div className="text-[10px] text-gray-700 space-y-0.5">
                    <p>เลขที่เอกสาร: <span className="font-bold">{docNo}</span></p>
                    <p>วันที่ออกเอกสาร: <span className="font-bold">{new Date(docDate).toLocaleDateString('th-TH')}</span></p>
                  </div>
                </div>
              </div>

              {/* ข้อมูลลูกค้าในเอกสาร */}
              <div className="grid grid-cols-2 gap-4 py-6 border-b border-gray-200">
                <div className="space-y-1">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">ลูกค้าผู้รับบริการ (Client Info)</p>
                  <p className="text-xs font-bold text-gray-900">{customerName}</p>
                  <p className="text-[10px] text-gray-600 leading-relaxed">{customerAddress}</p>
                  <p className="text-[10px] text-gray-600">📞 โทร: {customerPhone}</p>
                </div>
                <div className="space-y-1 text-right self-end">
                  {docType === 'tax_invoice' && customerTaxId && (
                    <p className="text-[10px] text-gray-800">
                      เลขประจำตัวผู้เสียภาษี: <span className="font-bold">{customerTaxId}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* ตารางแสดงรายการ */}
              <table className="w-full text-left text-xs border-collapse mt-6">
                <thead>
                  <tr className="bg-gray-50 border-y border-gray-300 text-gray-700 font-bold">
                    <th className="p-3 pl-4 text-center w-12">ลำดับ</th>
                    <th className="p-3">คำอธิบายรายการ (Description)</th>
                    <th className="p-3 text-center w-16">จำนวน</th>
                    <th className="p-3 text-right w-28">ราคาต่อหน่วย</th>
                    <th className="p-3 text-right pr-4 w-28">จำนวนเงิน (บาท)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {billItems.map((item, index) => {
                    const amount = Number(item.qty) * Number(item.unitPrice) || 0;
                    return (
                      <tr key={item.id}>
                        <td className="p-3 text-center text-gray-500 font-medium">{index + 1}</td>
                        <td className="p-3 font-semibold text-gray-800">{item.description || '-'}</td>
                        <td className="p-3 text-center font-medium">{item.qty}</td>
                        <td className="p-3 text-right font-medium">{Number(item.unitPrice).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                        <td className="p-3 text-right pr-4 font-bold text-gray-900">{amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ส่วนสรุปการเงินด้านล่าง */}
            <div className="mt-8 space-y-6">
              
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                
                {/* คำอ่านภาษาไทย */}
                <div className="md:col-span-7 bg-gray-50 p-3 rounded-lg border border-gray-200 text-center font-bold text-[10px] text-gray-600 flex justify-center items-center h-full min-h-12">
                  ({thaiBahtText(netAmount)})
                </div>

                {/* ตัวเลขคำนวณ */}
                <div className="md:col-span-5 text-right space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">ราคารวม (Subtotal):</span>
                    <span className="font-bold">฿{subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  {vatType !== 'none' && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">ฐานภาษี (Base Amount):</span>
                      <span>฿{baseAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  {vatType !== 'none' && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">ภาษีมูลค่าเพิ่ม (VAT 7%):</span>
                      <span>฿{vat.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  {withholdingTax > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span className="font-medium">หักภาษี ณ ที่จ่าย ({withholdingTax}%):</span>
                      <span>- ฿{whtAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm border-t border-gray-300 pt-2 text-indigo-700 font-black">
                    <span>ยอดสุทธิที่ต้องชำระ (Net Total):</span>
                    <span>฿{netAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>

              {/* วิธีแก้ปัญหาการลายเซ็นเมื่ออยู่คนละที่ (ออนไลน์ / ดิจิทัล) */}
              <div className="pt-6 border-t border-gray-100">
                {signatureType === 'online' && (
                  <div className="flex flex-col items-center justify-center py-4 bg-emerald-50 border border-emerald-200 rounded-xl max-w-sm mx-auto text-center">
                    {/* ตราอนุมัติออนไลน์ e-Stamp */}
                    <div className="border-4 border-emerald-600 text-emerald-600 font-black text-xs px-4 py-2 uppercase rounded-lg transform -rotate-3 inline-block tracking-wider">
                      ชำระเงินเรียบร้อย
                      <p className="text-[8px] font-bold text-center">PAID / ONLINE SYSTEM</p>
                    </div>
                    <p className="text-[8px] text-emerald-700 font-bold mt-2">
                      เอกสารฉบับนี้ออกด้วยระบบอิเล็กทรอนิกส์
                    </p>
                    <p className="text-[7px] text-gray-400">
                      ยืนยันการชำระเงินของลูกค้าและสรุปรายการบริการเรียบร้อยแล้ว
                    </p>
                  </div>
                )}

                {signatureType === 'manual' && (
                  <div className="grid grid-cols-2 gap-8 text-center text-xs">
                    <div className="space-y-12">
                      <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wide">ผู้รับเงิน (Recipient Signature)</p>
                      <div className="border-t border-dashed border-gray-400 w-44 mx-auto pt-2">
                        <p className="font-bold text-gray-800">วันที่ ......./......./.......</p>
                      </div>
                    </div>
                    <div className="space-y-12">
                      <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wide">ผู้มีอำนาจลงนาม (Authorized Signature)</p>
                      <div className="border-t border-dashed border-gray-400 w-44 mx-auto pt-2">
                        <p className="font-bold text-gray-800">ผู้รับมอบอำนาจ</p>
                      </div>
                    </div>
                  </div>
                )}

                {signatureType === 'foot_note' && (
                  <div className="text-center py-4 text-gray-500 text-[10px] italic space-y-1 max-w-md mx-auto">
                    <p className="font-bold">🚨 คำชี้แจงสถานะเอกสารแบบออนไลน์</p>
                    <p>“เอกสารฉบับนี้เป็นเอกสารรับเงินที่จัดทำผ่านระบบคอมพิวเตอร์ส่วนกลางแบบ e-Receipt การยืนยันชำระเงินและการส่งมอบบริการถือเป็นอันเสร็จสิ้นโดยอัตโนมัติ จึงไม่จำเป็นต้องมีลายมือชื่อผู้ลงนามทางกายภาพ”</p>
                  </div>
                )}
              </div>

            </div>

          </div>

        </div>
      )}

    </div>
  );
}
